import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'
import { FormattedMessage } from 'react-intl'

import { getGlobalParams, getCustomIntl } from 'config/config'
import { divideBy100 } from 'utils'
import { dispatchFundDetailEvent, dispatchMonthlyDownloadEvent } from 'utils/tracking'
import { handleComparisonItems } from 'utils/interaction'

import { Button } from 'gdpp-components/dist/cjs/components/atoms'

import GridContainer from '../../atoms/FoundationGrid/GridContainer'
import Grid from '../../atoms/FoundationGrid/Grid'
import Cell from '../../atoms/FoundationGrid/Cell'
import Select from '../../atoms/Select'
import MorningStar from '../../atoms/MorningStar'
import Number from '../../atoms/Number'
import Date from '../../atoms/Date'
import List from '../../atoms/List'
import Modal from '../../molecules/Modal'

import './style.scss'

const GeneralInfo = ({ extraClasses, generalInfo }) => {
  const { favListName, fundComparisonCart, terms, hasFundComparison, cart2FundJob, comparisonLimit } = getGlobalParams()
  const intl = getCustomIntl()
  const favItems = localStorage.getItem(favListName) ? JSON.parse(localStorage.getItem(favListName)) : []

  const [isAddedToFav, setFavStatus] = useState(favItems.includes(generalInfo.shareClassId))
  const [isAddedToCart, setCartStatus] = useState()
  const [isComparisonDisabled, setDisabledStatus] = useState()

  const isAddedToCartRef = useRef(isAddedToCart)

  const setCartStatusRef = (x) => {
    isAddedToCartRef.current = x
    setCartStatus(x)
  }

  const isComparisonDisabledRef = useRef(isComparisonDisabled)

  const setDisabledStatusRef = (x) => {
    isComparisonDisabledRef.current = x
    setDisabledStatus(x)
  }

  const handleAddToFav = () => {
    if (isAddedToFav) {
      _.remove(favItems, (favItem) => generalInfo.shareClassId === favItem)
    } else {
      favItems.push(generalInfo.shareClassId)
    }

    localStorage.setItem(favListName, JSON.stringify(favItems))
    setFavStatus(!isAddedToFav)

    dispatchFundDetailEvent(
      'watchlist',
      intl.formatMessage({
        id: `fundDetail.${isAddedToFav ? 'removeFromFavList' : 'addToFavList'}`
      })
    )
  }

  const toggleToCart = () => {
    handleComparisonItems({ shareClassId: generalInfo.shareClassId, shareClassName: generalInfo.shareClassNameZh, isFromDetailPage: true })
    setCartStatusRef(!isAddedToCartRef.current)
  }

  const handlePurchase = () => {
    dispatchFundDetailEvent('buy', 'buy fund button click')
  }

  const handleMonthlyReprot = (monthlyReportLink) => {
    dispatchMonthlyDownloadEvent(_.last(monthlyReportLink.split('/')))
    window.open(monthlyReportLink, '_blank', 'noreferrer, noopener')
  }

  const ShareClassInfo = () => {
    const { shareClassItemObj, taCode, isin, monthlyReportLink, isSoftClose, focusFundPDF } = generalInfo
    const { downloadLabel, downloadUrl } = focusFundPDF
    const { shareClassItem, selectedShareClassItem, onHandleChange } = shareClassItemObj
    return (
      <Grid extraClasses="align-middle grid-padding-x general-info__share-classes">
        <Cell extraClasses="large-5">
          <Grid extraClasses="align-middle grid-margin-x">
            <Cell extraClasses="shrink hide-for-small-only">
              <label id="fil-general-info__shareclass-label" className="text-body-m">
                <FormattedMessage id="fundDetail.selectShareClass" />
              </label>
            </Cell>
            <Cell extraClasses="auto">
              <Select
                id="fil-general-info__shareClass-selector-elem"
                value={selectedShareClassItem}
                options={JSON.stringify(shareClassItem)}
                onChange={(e) => {
                  onHandleChange(e)
                }}
                inversed="true"
              />
            </Cell>
          </Grid>
        </Cell>
        <Cell extraClasses="large-7 text-body-m">
          <Grid extraClasses="align-middle grid-padding-x grid-padding-y general-info__fund-code">
            <Cell id="fil-general-info__fund-code-taCode" extraClasses="medium-shrink">
              <FormattedMessage id="fundDetail.fundCode" />: {taCode}
            </Cell>
            <Cell id="fil-general-info__fund-code-isin" extraClasses="medium-shrink">
              <FormattedMessage id="fundDetail.isinContent" />: {isin}
            </Cell>
            {monthlyReportLink && (
              <Cell
                id="fil-general-info__cta-monthly-report"
                extraClasses={classnames('medium-shrink small-height-none', {
                  displayInSoftClose: isSoftClose
                })}
              >
                <Button
                  kind="textlink"
                  onClick={() => {
                    handleMonthlyReprot(monthlyReportLink)
                  }}
                  icon="office-file-pdf-1"
                  iconPosition="left"
                  iconSize="20"
                  label={intl.formatMessage({ id: 'fundDetail.downloadMonthlyReport' })}
                  extraClasses="show-for-xlarge"
                />
              </Cell>
            )}
            {downloadUrl && (
              <Cell
                id="fil-general-info__focus-fund-pdf"
                extraClasses={classnames('medium-shrink small-height-none', {
                  displayInSoftClose: isSoftClose
                })}
              >
                <Button
                  kind="textlink"
                  onClick={() => {
                    handleMonthlyReprot(downloadUrl)
                  }}
                  icon="office-file-pdf-1"
                  iconPosition="left"
                  iconSize="20"
                  label={downloadLabel}
                  extraClasses="show-for-xlarge"
                />
              </Cell>
            )}
          </Grid>
        </Cell>
        <Cell extraClasses="text-body-m show-for-large hide-for-xlarge">
          <Grid extraClasses="align-left grid-padding-x grid-padding-y general-info__fund-buttons-large">
            {monthlyReportLink && (
              <Cell
                id="fil-general-info__cta-monthly-report"
                extraClasses={classnames('medium-shrink', {
                  displayInSoftClose: isSoftClose
                })}
              >
                <Button
                  kind="textlink"
                  onClick={() => {
                    handleMonthlyReprot(monthlyReportLink)
                  }}
                  icon="office-file-pdf-1"
                  iconPosition="left"
                  iconSize="20"
                  label={intl.formatMessage({ id: 'fundDetail.downloadMonthlyReport' })}
                  extraClasses="show-for-large hide-for-xlarge"
                />
              </Cell>
            )}
            {downloadUrl && (
              <Cell
                id="fil-general-info__cta-monthly-report"
                extraClasses={classnames('medium-shrink', {
                  displayInSoftClose: isSoftClose
                })}
              >
                <Button
                  kind="textlink"
                  onClick={() => {
                    handleMonthlyReprot(downloadUrl)
                  }}
                  icon="office-file-pdf-1"
                  iconPosition="left"
                  iconSize="20"
                  label={downloadLabel}
                  extraClasses="show-for-large hide-for-xlarge"
                />
              </Cell>
            )}
          </Grid>
        </Cell>
        <Cell extraClasses="text-body-m hide-for-xlarge hide-for-large">
          <Grid extraClasses="align-left grid-padding-x grid-padding-y general-info__fund-buttons-small">
            {monthlyReportLink && (
              <Cell
                id="fil-general-info__cta-monthly-report"
                extraClasses={classnames('shrink', {
                  displayInSoftClose: isSoftClose
                })}
              >
                <Button
                  kind="textlink"
                  onClick={() => {
                    handleMonthlyReprot(monthlyReportLink)
                  }}
                  icon="office-file-pdf-1"
                  iconPosition="left"
                  iconSize="20"
                  size="small"
                  label={intl.formatMessage({ id: 'fundDetail.downloadMonthlyReport' })}
                  extraClasses="hide-for-xlarge hide-for-large"
                />
              </Cell>
            )}
            {downloadUrl && (
              <Cell
                id="fil-general-info__cta-monthly-report"
                extraClasses={classnames('shrink', {
                  displayInSoftClose: isSoftClose
                })}
              >
                <Button
                  kind="textlink"
                  onClick={() => {
                    handleMonthlyReprot(downloadUrl)
                  }}
                  icon="office-file-pdf-1"
                  iconPosition="left"
                  iconSize="20"
                  size="small"
                  label={downloadLabel}
                  extraClasses="hide-for-xlarge hide-for-large"
                />
              </Cell>
            )}
          </Grid>
        </Cell>
      </Grid>
    )
  }

  const RiskMoringStarCell = () => {
    const { riskMorningStarInfo } = generalInfo
    const modalContentList = terms.fundDetail.riskRatingModal.modalList
    return (
      <Grid extraClasses="general-info__risk-morningstar">
        <Cell extraClasses="medium-6 large-12 general-info__rating">
          <span id="fil-general-info__rating-title" className="text-body-s">
            <FormattedMessage id="fundDetail.riskRatingModal.title" />:
          </span>
          <Modal
            {...{
              modalHeader: intl.formatMessage({ id: 'fundDetail.riskRatingModal.title' }),
              extraClasses: 'general-info__risk-morningstar-modal',
              size: 'small',
              zIndex: 100,
              kind: 'tooltip'
            }}
          >
            <React.Fragment>
              <List contents={modalContentList} />
              <span dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'fundDetail.riskRatingModal.content' }) }} />
            </React.Fragment>
          </Modal>
          <div id="fil-general-info__rating-level" className="text-body-s font-en">
            <strong>{riskMorningStarInfo.riskLevel}</strong>
          </div>
        </Cell>
        <Cell extraClasses="medium-6 large-12">
          <div id="fil-general-info__ms-title" className="text-body-s general-info__title">
            <FormattedMessage id="fundList.group.categories.MORNINGSTAR_RATING" />:
          </div>
          <MorningStar
            id="fil-general-info__ms-level"
            starnum={riskMorningStarInfo.morningStarCount}
            starSize={riskMorningStarInfo.morningStarSize}
          />
        </Cell>
      </Grid>
    )
  }

  const PriceInfoCell = () => {
    const { netValueInfo, navPriceDate, redIsRaise } = generalInfo
    return (
      <Grid extraClasses="general-info__price-info">
        <Cell extraClasses="medium-4 large-4 general-info__net-price">
          <div id="fil-general-info__net-price-title" className="text-body-s general-info__title">
            <FormattedMessage
              id="fundDetail.netValueTitle"
              values={{
                netValueCurrency: netValueInfo.currency
              }}
            />
          </div>
          <Number
            id="fil-general-info__net-price-value"
            currency={{
              style: 'currency',
              currency: netValueInfo.currencyCode,
              currencyDisplay: 'narrowSymbol'
            }}
            value={netValueInfo.currentValue}
            fluctuationColor={false}
            fractionDigits={4}
            extraClasses="text-heading-m general-info__value font-en"
          />
        </Cell>
        <Cell extraClasses="medium-auto large-auto general-info__percentage">
          <div id="fil-general-info__daily-change-title" className="text-body-s general-info__title">
            <FormattedMessage
              id="fundDetail.daliyChange"
              values={{
                netValueCurrency: netValueInfo.currency
              }}
            />
          </div>
          <div className="text-heading-m general-info__value font-en">
            <Number
              id="fil-general-info__daily-change-value"
              currency={{
                style: 'currency',
                currency: netValueInfo.currencyCode,
                currencyDisplay: 'narrowSymbol',
                signDisplay: 'exceptZero'
              }}
              fractionDigits={4}
              value={netValueInfo.changeValue}
              redIsRaise={redIsRaise}
            />
            <Number
              id="fil-general-info__daily-change-percentage"
              {...{
                style: 'percent',
                value: divideBy100(netValueInfo.changePercent),
                signDisplay: 'exceptZero',
                redIsRaise
              }}
            >
              {(parts) => {
                const formattedNumber = _(parts)
                  .map((part) => part.value)
                  .join('')
                return `(${formattedNumber})`
              }}
            </Number>
          </div>
        </Cell>
        <Cell extraClasses="text-body-s general-info__publish-date font-en">
          <Date id="fil-general-info__publish-date" customFormat="yyyy-MM-dd" value={navPriceDate} />
        </Cell>
      </Grid>
    )
  }

  const DownloadCell = () => {
    const { purchaseLink, isSoftClose } = generalInfo
    return (
      <Grid extraClasses="general-info__cta align-middle">
        {!isSoftClose && (
          <React.Fragment>
            <Cell id="fil-general-info__cta-login" extraClasses="medium-shrink xlarge-shrink general-info__cta-item">
              <Button
                onClick={handlePurchase}
                label={intl.formatMessage({ id: 'fundDetail.loginToPurchase' })}
                url="https://secure.fidelity.com.tw/accounts/login"
                extraClasses="show-for-large"
              />
              <Button
                onClick={handlePurchase}
                label={intl.formatMessage({ id: 'fundDetail.loginToPurchase' })}
                url="https://secure.fidelity.com.tw/accounts/login"
                size="small"
                extraClasses="hide-for-large"
              />
            </Cell>
            <Cell id="fil-general-info__cta-other-insto" extraClasses="medium-shrink xlarge-6 xxlarge-7 general-info__cta-item">
              <Button
                url={purchaseLink}
                kind="tertiary"
                label={intl.formatMessage({ id: 'fundDetail.otherInstoPurchase' })}
                icon="expand-6"
                iconSize="16"
                extraClasses="show-for-large"
              />
              <Button
                url={purchaseLink}
                kind="tertiary"
                label={intl.formatMessage({ id: 'fundDetail.otherInstoPurchase' })}
                icon="expand-6"
                iconSize="16"
                size="small"
                extraClasses="hide-for-large"
              />
            </Cell>
          </React.Fragment>
        )}
        <Cell
          id="fil-general-info__cta-add-to-fav"
          extraClasses={classnames('shrink general-info__cta-item', {
            displayInSoftClose: isSoftClose
          })}
        >
          <Button
            kind="textlink"
            icon="rating-star-ribbon"
            iconPosition="left"
            iconSize="20"
            label={intl.formatMessage({
              id: `fundDetail.${isAddedToFav ? 'removeFromFavList' : 'addToFavList'}`
            })}
            extraClasses="text-left show-for-large"
            onClick={handleAddToFav}
          />
          <Button
            kind="textlink"
            icon="rating-star-ribbon"
            iconPosition="left"
            iconSize="20"
            label={intl.formatMessage({
              id: `fundDetail.${isAddedToFav ? 'removeFromFavList' : 'addToFavList'}`
            })}
            extraClasses="text-left hide-for-large"
            onClick={handleAddToFav}
            size="small"
          />
        </Cell>
        {hasFundComparison && (
          <Cell
            id="fil-general-info__cta-add-to-cart"
            extraClasses={classnames('shrink medium-auto general-info__cta-item xlarge-6', {
              displayInSoftClose: isSoftClose
            })}
          >
            <Button
              kind="textlink"
              icon="data-file-bars-add"
              iconPosition="left"
              iconSize="20"
              label={intl.formatMessage({
                id: `fundList.menu.shoppingCart.${isAddedToCartRef.current ? 'remove' : 'add'}`
              })}
              extraClasses="text-left show-for-large"
              onClick={toggleToCart}
              {...(isComparisonDisabledRef.current ? { disabled: isComparisonDisabledRef.current } : {})}
            />
            <Button
              kind="textlink"
              icon="data-file-bars-add"
              iconPosition="left"
              iconSize="20"
              label={intl.formatMessage({
                id: `fundList.menu.shoppingCart.${isAddedToCartRef.current ? 'remove' : 'add'}`
              })}
              extraClasses="text-left hide-for-large"
              onClick={toggleToCart}
              size="small"
              {...(isComparisonDisabledRef.current ? { disabled: isComparisonDisabledRef.current } : {})}
            />
          </Cell>
        )}
      </Grid>
    )
  }

  useEffect(() => {
    const cartItems = sessionStorage.getItem(fundComparisonCart) ? JSON.parse(sessionStorage.getItem(fundComparisonCart)) : []
    const addedToCartStatus = !!_.find(cartItems, (item) => item.shareClassId === generalInfo.shareClassId)

    setCartStatusRef(addedToCartStatus)
    setDisabledStatusRef(cartItems.length === comparisonLimit && !addedToCartStatus)

    if (hasFundComparison) {
      cart2FundJob.cart2FundSubject.subscribe({
        next: () => {
          const currentCartItems = sessionStorage.getItem(fundComparisonCart) ? JSON.parse(sessionStorage.getItem(fundComparisonCart)) : []

          const currentAddedToCartStatus = !!_.find(
            currentCartItems,
            (filteredItem) => filteredItem.shareClassId === generalInfo.shareClassId
          )

          setDisabledStatusRef(currentCartItems.length === comparisonLimit && !currentAddedToCartStatus)
          setCartStatusRef(currentAddedToCartStatus)
        }
      })
    }
  }, [])

  return (
    <div id="fil-general-info" className={classnames('general-info text-left', extraClasses)}>
      <GridContainer>
        <Grid>
          <Cell>
            <h3 id="fil-general-info__name" className="text-heading-m margin-bottom-1 font-normal general-info__name">
              {generalInfo.fundName}
            </h3>
          </Cell>
          {generalInfo.followingDisclaimer && (
            <Cell>
              <h3 id="fil-general-info__disclaimer" className="text-heading-m margin-bottom-3 font-normal general-info__disclaimer">
                {generalInfo.followingDisclaimer}
              </h3>
            </Cell>
          )}
          {generalInfo.englishName && (
            <Cell>
              <p id="fil-general-info__english-name" className="text-body-m margin-bottom-4 general-info__english-name">
                {generalInfo.englishName}
              </p>
            </Cell>
          )}
        </Grid>
        <ShareClassInfo />
        <Grid extraClasses="bg-brand-white general-info__fund-view">
          <Cell>
            <GridContainer variation="full" extraClasses="full">
              <Grid>
                <Cell extraClasses="large-3 xlarge-2 general-info__item">
                  <RiskMoringStarCell />
                </Cell>
                <Cell extraClasses="large-9 xlarge-auto general-info__item">
                  <PriceInfoCell />
                </Cell>
                <Cell extraClasses="medium-12 xlarge-4 general-info__item align-self-middle">
                  <DownloadCell />
                </Cell>
              </Grid>
            </GridContainer>
          </Cell>
        </Grid>
      </GridContainer>
    </div>
  )
}

GeneralInfo.propTypes = {
  generalInfo: PropTypes.object.isRequired,
  extraClasses: PropTypes.string
}

GeneralInfo.defaultProps = {
  extraClasses: ''
}

export default GeneralInfo
